import { ArrowUpRight, BookOpen, Mail, MessageSquare, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useUserProfile } from '@repo/queries';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const SUPPORT_EMAIL = 'help@invoyss.com';

export function SupportScreen() {
  const { profile } = useUserProfile();

  const subject = encodeURIComponent('Invoyss web — support request');
  const body = encodeURIComponent(
    [
      'What happened:',
      '',
      'What I expected:',
      '',
      'Steps to reproduce:',
      '',
      `User: ${profile?.email ?? '(unknown)'}`,
      `Surface: web`,
    ].join('\n'),
  );

  return (
    <Page>
      <PageHeader
        title="Support"
        description="Need a hand? Here's the quickest way to reach us."
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-2">
              <CardTitle className="text-base">Email support</CardTitle>
              <Badge variant="success">Usually under 1 business day</Badge>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              Best for account, billing, and anything you'd rather not hash out in chat.
            </p>
            <Button asChild>
              <a href={`mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`}>
                <Mail className="size-4" /> Email {SUPPORT_EMAIL}
              </a>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Send feedback</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              Have an idea, found a bug, or want to say something nice? It all goes
              straight to the product team.
            </p>
            <Button asChild variant="outline">
              <Link to="/settings/feedback">
                <MessageSquare className="size-4" /> Open feedback form
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Help docs</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              Setup guides, money-handling explainers, and the always-current changelog.
            </p>
            <Button asChild variant="outline">
              <a href="https://invoyss.com/docs" target="_blank" rel="noreferrer">
                <BookOpen className="size-4" /> Read the docs
                <ArrowUpRight className="size-3.5" />
              </a>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Status</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              Up-to-the-minute uptime for the API, render pipeline, and email delivery.
            </p>
            <Button asChild variant="outline">
              <a href="https://status.invoyss.com" target="_blank" rel="noreferrer">
                <ShieldCheck className="size-4" /> View status
                <ArrowUpRight className="size-3.5" />
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>
    </Page>
  );
}
